/**
 * @description
 * Sync history panel component for displaying past sync operations.
 * Shows sync type, status, timestamp, and records processed.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/pages/Settings.tsx
 */

import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import type { SyncHistoryEntry } from './types';

interface SyncHistoryPanelProps {
  clientId?: string;
  limit?: number;
}

/**
 * Component for displaying sync history
 */
export const SyncHistoryPanel: React.FC<SyncHistoryPanelProps> = ({
  clientId,
  limit = 10,
}) => {
  const [history, setHistory] = useState<SyncHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  /**
   * Fetch sync history from API
   */
  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (clientId) params.set('clientId', clientId);
      params.set('limit', limit.toString());

      const response = await fetch(`/api/platform-sync/history?${params}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sync history');
      }

      const data = await response.json() as SyncHistoryEntry[] | { history: SyncHistoryEntry[] };
      // Ensure we always set an array, handle both direct array and object with history property
      const historyData = Array.isArray(data) ? data : ((data as { history?: SyncHistoryEntry[] })?.history || []);
      setHistory(historyData);
    } catch (error) {
      console.error('Failed to fetch sync history:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get icon for sync status
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  /**
   * Format duration in milliseconds to readable string
   */
  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Sync History</h3>
        <button
          onClick={fetchHistory}
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {(!history || history.length === 0) ? (
        <div className="text-center py-8 text-muted-foreground">
          <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No sync history available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(history || []).map((entry) => (
            <div
              key={entry.id}
              className="p-4 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {getStatusIcon(entry.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-foreground">
                      {entry.syncType === 'client' ? 'Client Sync' : 'User Sync'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(entry.startedAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {entry.recordsProcessed} processed • {entry.recordsCreated} created •{' '}
                    {entry.recordsUpdated} updated
                    {entry.recordsFailed > 0 && ` • ${entry.recordsFailed} failed`}
                  </div>
                  {entry.durationMs && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Duration: {formatDuration(entry.durationMs)}
                    </div>
                  )}
                  {entry.errorMessage && (
                    <div className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                      {entry.errorMessage}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span className="px-2 py-1 rounded bg-muted">
                      {entry.triggerType}
                    </span>
                    <span>by {entry.triggeredBy}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */